import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Car, AlertTriangle, CheckCircle, Info, Gauge, Wrench, Droplet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VehicleLibraryPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const features = response.data?.plan?.features || response.data?.features || {};
      // Accept either the new key OR the legacy key
      const hasFeature = !!(
        features.vehicle_data_library === true ||
        features.extended_vehicle_database === true ||
        features.vehicle_data_library === 'true' ||
        features.extended_vehicle_database === 'true'
      );
      setHasAccess(hasFeature);

      if (!hasFeature) {
        toast.error('Diese Funktion ist nicht in Ihrem Plan enthalten');
      }
    } catch (error) {
      console.error('Access check failed', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm || searchTerm.length < 2) {
      toast.error('Bitte mindestens 2 Zeichen eingeben');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/user/vehicle-data/library?search=${encodeURIComponent(searchTerm)}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVehicles(response.data.specs || []);
      if (response.data.specs.length === 0) {
        toast.info('Keine Fahrzeuge gefunden');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
        toast.error('Diese Funktion ist nicht in Ihrem Plan enthalten');
      } else {
        toast.error('Fehler bei der Suche');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = async (vehicleId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/knowledge/vehicle-specs/${vehicleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedVehicle(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="max-w-5xl mx-auto">
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-12 text-center">
              <AlertTriangle size={64} className="mx-auto mb-6" style={{ color: '#F59E0B' }} />
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#E6EDF3' }}>
                Premium-Feature
              </h2>
              <p className="mb-6" style={{ color: '#94A3B8' }}>
                Die Fahrzeugdaten-Bibliothek ist ein Premium-Feature.
              </p>
              <Button
                onClick={() => navigate('/subscription')}
                style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
                size="lg"
              >
                Jetzt upgraden
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
            Fahrzeugdaten-Bibliothek
          </h2>
          <p style={{ color: '#94A3B8' }}>
            Durchsuchen Sie verifizierte Fahrzeugdaten
          </p>
        </div>

        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardContent className="p-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3" size={20} style={{ color: '#94A3B8' }} />
                <Input
                  placeholder="Suchen Sie nach Marke, Modell..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={loading}
                style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
              >
                {loading ? 'Suche...' : 'Suchen'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {!selectedVehicle && vehicles.length === 0 && (
          <Card style={{ backgroundColor: '#1EA0E620', borderColor: '#1EA0E6' }}>
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Info size={24} style={{ color: '#1EA0E6' }} />
                <div>
                  <h3 className="font-semibold mb-2" style={{ color: '#7CD4FD' }}>
                    So funktioniert's
                  </h3>
                  <p className="text-sm" style={{ color: '#E6EDF3' }}>
                    Geben Sie einfach Marke oder Modell ein
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!selectedVehicle && vehicles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((vehicle) => {
              const completeness = (vehicle.completeness_score || 0) * 100;
              return (
                <Card
                  key={vehicle.id}
                  className="cursor-pointer hover:border-blue-500 transition-colors"
                  style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
                  onClick={() => handleSelectVehicle(vehicle.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <Car size={32} style={{ color: '#1EA0E6' }} />
                      <Badge>{completeness.toFixed(0)}%</Badge>
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#E6EDF3' }}>
                      {vehicle.brand} {vehicle.model}
                    </h3>
                    {vehicle.engine && (
                      <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                        Motor: {vehicle.engine}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {selectedVehicle && (
          <div>
            <Button
              onClick={() => setSelectedVehicle(null)}
              variant="ghost"
              className="mb-4"
            >
              Zurück
            </Button>

            <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                  {selectedVehicle.brand} {selectedVehicle.model}
                </h2>
              </CardContent>
            </Card>

            {selectedVehicle.specifications?.engine && (
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#E6EDF3' }}>Motor & Öl</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(selectedVehicle.specifications.engine).map(([key, value]) => {
                      if (!value) return null;
                      return (
                        <div key={key} className="p-3 rounded" style={{ backgroundColor: '#0B0F14' }}>
                          <p className="text-xs mb-1" style={{ color: '#7CD4FD' }}>
                            {key.replace(/_/g, ' ')}
                          </p>
                          <p className="font-semibold" style={{ color: '#E6EDF3' }}>{value}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
